/**
 * VaultStorageUsage — Per-Holder storage usage bar (Phase 12e review fix #9).
 *
 * Mirrors the existing legacy `StorageUsage` shape so the My Data
 * dual-tab layout feels consistent across the Vault Documents tab
 * and the Legacy Storage tab — but reads from
 * `vault_documents_storage` (trigger-maintained) instead of
 * `user_storage`. The arche shell embeds it on the documents list
 * page; My Data embeds it inside the Vault Documents tab on
 * Personal Holders + on Org Holders.
 *
 * Visual treatment matches the legacy widget: horizontal bar with a
 * percentage label, "X GB of Y GB used · N file(s)." Approaching the
 * cap (>= 80%) and at the cap (>= 95%) shift the bar color.
 */

import { Card, CardContent } from '@/components/ui/card';
import { useVaultDocumentsStorage } from '@/arche/arches/vault-documents/hooks/useVaultDocuments';

interface VaultStorageUsageProps {
  holderId: string;
  /** Optional title override. Defaults to "Vault Documents storage". */
  title?: string;
  /** When true, render in a card. When false, render inline. */
  card?: boolean;
}

export function VaultStorageUsage({
  holderId,
  title = 'Vault Documents storage',
  card = true,
}: VaultStorageUsageProps) {
  const query = useVaultDocumentsStorage(holderId);

  const body = (() => {
    if (query.isLoading) {
      return <p className="text-sm text-muted-foreground">Loading storage…</p>;
    }
    if (query.isError || !query.data) {
      return <p className="text-sm text-muted-foreground">Couldn't load storage usage.</p>;
    }
    // Bar binds to the billing-principal aggregate — that's the
    // cap the server enforces at upload time. The per-Holder
    // breakdown is shown underneath when the principal owns more
    // than one Holder (Personal + Orgs).
    const { storage, principal } = query.data;
    const pct =
      principal.quotaBytes > 0
        ? Math.min(100, (principal.totalStorageBytes / principal.quotaBytes) * 100)
        : 0;
    const tone = pct >= 95 ? 'bg-destructive' : pct >= 80 ? 'bg-amber-500' : 'bg-primary';
    const showHolderBreakdown = principal.totalStorageBytes !== storage.totalStorageBytes;
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-foreground">{title}</span>
          <span className="text-muted-foreground">{pct.toFixed(1)}%</span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded bg-muted">
          <div className={`h-full transition-all ${tone}`} style={{ width: `${pct}%` }} />
        </div>
        <p className="text-xs text-muted-foreground">
          {formatBytes(principal.totalStorageBytes)} of {formatBytes(principal.quotaBytes)} used
          {' · '}
          {principal.fileCount} {principal.fileCount === 1 ? 'document' : 'documents'}
        </p>
        {showHolderBreakdown && (
          <p className="text-xs text-muted-foreground">
            This Holder: {formatBytes(storage.totalStorageBytes)} · {storage.fileCount}{' '}
            {storage.fileCount === 1 ? 'document' : 'documents'}
          </p>
        )}
        {/* Over the plan's limit — e.g. after a downgrade (file areas §4.10).
            Nothing is deleted; new files wait until there is room. */}
        {principal.totalStorageBytes > principal.quotaBytes && (
          <p role="status" className="text-xs text-amber-700 dark:text-amber-400">
            You’re over your plan’s storage. Nothing has been deleted — free up space or upgrade
            your plan to add more.
          </p>
        )}
      </div>
    );
  })();

  if (!card) return body;
  return (
    <Card>
      <CardContent className="pt-6">{body}</CardContent>
    </Card>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
